import axios from "axios";
import { API_URL } from "const/constants";

/** Partner statuses a client can be attributed to (mirrors StaffReferrerProfiles on the backend). */
export const ASSIGNABLE_PARTNER_STATUSES = ["ACTIVE", "PRE_ENROLMENT", "INVITED", "ONBOARDING"];

const PARTNER_NOTE = {
  PRE_ENROLMENT: "not enrolled yet",
  INVITED: "invited",
  ONBOARDING: "enrolling",
};

const emailKey = (email) =>
  String(email || "")
    .trim()
    .toLowerCase();

/**
 * Referral picker options for Add Client and the Change referrer dialog.
 *
 * Partners come first and carry their id. A staff member who already has a partner profile
 * (enrolled or pre-enrolment) is offered once, as that partner; other staff carry their user id and
 * the backend gives them a pre-enrolment profile. Suspended and archived partners are not offered.
 * Staff are offered only when `includeStaff` is set, which is when the backend allows non-referral
 * users to be referrers; the backend refuses them otherwise.
 */
export const buildReferralOptions = ({ partners = [], users = [], includeStaff = false }) => {
  const offered = partners.filter((p) => ASSIGNABLE_PARTNER_STATUSES.includes(p.status));
  const partnerEmails = new Set(offered.map((p) => emailKey(p.email)));
  return [
    ...offered.map((p) => ({
      id: `partner-${p.id}`,
      partnerId: p.id,
      userId: null,
      label: p.displayName,
      sublabel: PARTNER_NOTE[p.status] ? `${p.email || ""} · ${PARTNER_NOTE[p.status]}` : p.email,
      kind: "PARTNER",
    })),
    ...(includeStaff ? users : [])
      // A partner holds a REFERRAL_USER membership, so GET /users returns them here too; their
      // partner row above is the one that carries the id.
      .filter((u) => u.type !== "REFERRAL_USER")
      .filter((u) => !partnerEmails.has(emailKey(u.email)))
      .map((u) => ({
        id: `user-${u.id}`,
        partnerId: null,
        userId: u.id,
        label: u.name,
        sublabel: u.email,
        email: u.email,
        kind: "STAFF",
      })),
  ];
};

export const referralRequestFromSelection = (selection) => {
  if (!selection) {
    return { referralPartnerId: null, referralUserId: null, referral: "", customReferral: false };
  }
  if (selection.inputValue || typeof selection === "string") {
    return {
      referralPartnerId: null,
      referralUserId: null,
      referral: selection.inputValue || selection,
      customReferral: true,
    };
  }
  if (selection.kind === "PARTNER") {
    return {
      referralPartnerId: selection.partnerId,
      referralUserId: null,
      referral: selection.label,
      customReferral: false,
    };
  }
  return {
    referralPartnerId: null,
    referralUserId: selection.userId ?? null,
    referral: selection.email || selection.label,
    customReferral: false,
  };
};

export const isReferralLocked = (client) => Boolean(client?.referralLocked);

/** Partners a client can be attributed to; the server returns only assignable ones. */
export const fetchAssignableReferralPartners = async () => {
  const { data } = await axios.get(`${API_URL}/referral-partners/assignable`, {
    params: { page: 0, size: 500 },
  });
  const page = data?.data ?? data;
  return page?.content ?? [];
};
